import React from 'react';
import { capitalize, defaultTo, startCase } from 'lodash';
import Lozenge from '@atlaskit/lozenge';
import { Flex, xcss } from '@atlaskit/primitives';
import { FormHeader, FormFooter } from '@atlaskit/form';
import Button from '@atlaskit/button';
import { PricingPlan } from '@css-xp/graphtypes/src/generated-types-new';
import { useSidebar } from '../../../../hooks';
import EntitlementIcon from '../../../../entitlement-icon';
import { PageTabs } from '../../../../application/src/page-tabs/page-tabs';
import { AppliedPricingDetailsTab, PricingHistoryDetailsTab } from '../../../../common-tabs/pricing-details-tab';
import './view-pricing.scss';

interface Props {
  entitlementId: string;
  transactionId?: string;
  entitlementNumber?: string | null;
  billingFrequency?: string | null;
  productId?: string;
  productName?: string | null;
  offeringName?: string | null;
  pricingPlan?: PricingPlan | null;
  activeUsers?: number | null;
  isInvoicePage?: boolean;
  isApp?: boolean;
  logoId?: string;
  invoiceRedirectPath?: string;
  hidePricingCalculatorText?: boolean;
  version?: number;
}

const subHeadingStyles = xcss({
  fontSize: '11px',
  color: 'color.text.subtlest',
});

export const ViewPricing = ({
  entitlementId,
  transactionId,
  entitlementNumber,
  billingFrequency,
  productId,
  productName,
  offeringName,
  pricingPlan,
  activeUsers,
  logoId = '',
  isInvoicePage = false,
  invoiceRedirectPath,
  hidePricingCalculatorText = false,
  version,
}: Props) => {
  const { closeSidebar } = useSidebar();
  const isVersionTwo = version === 2;

  const appliedPricingDetailsTab = () => {
    return (
      <AppliedPricingDetailsTab
        label="Applied pricing"
        offeringName={offeringName}
        billingFrequency={billingFrequency}
        pricingPlan={pricingPlan}
        activeUsers={activeUsers}
        productName={productName}
        isInvoicePage={isInvoicePage}
        hidePricingCalculatorText={hidePricingCalculatorText}
        hideCurrencyFromValues={isVersionTwo}
        shouldAlignAmountCellsRight={isVersionTwo}
        pricingPlanVersion={isVersionTwo ? 2 : undefined}
      />
    );
  };

  const pricingPlanType = pricingPlan?.type;

  const getPricingDetailsTab = () => {
    return isInvoicePage ? (
      <div className="pricing-details-tab-container">
        {isVersionTwo || <div className="applied-pricing-sub-head">Applied pricing</div>}
        {appliedPricingDetailsTab()}
      </div>
    ) : (
      <PageTabs className="pricing-details-tab-container" showAllTab={false}>
        {appliedPricingDetailsTab()}
        <PricingHistoryDetailsTab
          label="Pricing history"
          transactionId={transactionId}
          entitlementId={entitlementId}
          invoiceRedirectPath={invoiceRedirectPath}
        />
      </PageTabs>
    );
  };

  if (isVersionTwo) {
    return (
      <div data-testid="view-pricing-details-tab" className="view-pricing-details-tab-v2">
        <FormHeader>
          <Flex gap="space.050">
            <EntitlementIcon logoId={logoId} productId={productId} isCloud={true} className="avatar medium" />
            <div>
              <h3 style={{ display: 'inline' }}>{`${defaultTo(productName, 'Unnamed product')}`}</h3>
              <Flex xcss={subHeadingStyles} as="span">
                {entitlementNumber}
              </Flex>
            </div>
          </Flex>
        </FormHeader>
        {getPricingDetailsTab()}
      </div>
    );
  }

  return (
    <div data-testid="view-pricing-details-tab">
      <FormHeader title="Pricing">
        <EntitlementIcon logoId={logoId} productId={productId} isCloud={true} className="avatar medium" />{' '}
        <h4 style={{ display: 'inline' }}>{`${defaultTo(productName, 'Unnamed product')} ${entitlementNumber}`}</h4>
        &nbsp;&nbsp;
        {pricingPlanType && (
          <Lozenge appearance="default">{capitalize(startCase(pricingPlanType + ' ' + 'pricing' ?? ''))}</Lozenge>
        )}
      </FormHeader>
      {getPricingDetailsTab()}
      <FormFooter align="end">
        <Button
          appearance="primary"
          aria-label="Close view pricing"
          className="cancel-view-pricing"
          onClick={closeSidebar}
        >
          Close
        </Button>
      </FormFooter>
    </div>
  );
};
